import React, { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import ReactMarkdown from 'react-markdown';
import './LLMoid.scss';
import SEO from '../../components/SEO/SEO';

const LLMoid = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const chatContainerRef = useRef(null);

  // API endpoint - update this with your actual endpoint
  const API_ENDPOINT = 'https://llmoid.vercel.app/ask';

  useEffect(() => {
    if (!containerRef.current) return;

    const h1 = containerRef.current.querySelector('h1');
    const introP = containerRef.current.querySelector(
      '.llmoid-page-content > p'
    );
    const chatContainer = chatContainerRef.current;

    if (!h1 || !introP) return;

    // Set initial state
    gsap.set([h1, introP], {
      opacity: 0,
      y: 30,
    });

    if (chatContainer) {
      gsap.set(chatContainer, {
        opacity: 0,
        y: 50,
      });
    }

    const tl = gsap.timeline();

    // Animate heading
    tl.to(
      h1,
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      },
      '-=0.4'
    );

    // Animate intro paragraph
    tl.to(
      introP,
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      },
      '-=0.4'
    );

    // Animate chat container
    if (chatContainer) {
      tl.to(
        chatContainer,
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'power3.out',
        },
        '-=0.4'
      );
    }

    return () => {
      tl.kill();
    };
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Add user message to chat
    const newUserMessage = {
      id: Date.now(),
      type: 'user',
      text: userMessage,
    };

    setMessages(prev => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          history: messages,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Add AI response to chat
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        text: data.answer || 'Sorry, I could not process your request.',
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);

      // Add error message to chat
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        text: 'Sorry, there was an error processing your request. Please try again.',
        isError: true,
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = e => {
    setInputMessage(e.target.value);
  };

  return (
    <>
      <SEO
        title="LLMoid - AI Chat - Moid Hasan Beig | Portfolio"
        description="Chat with LLMoid, an AI assistant. Ask questions and get intelligent responses powered by AI."
        keywords="LLMoid, AI chat, artificial intelligence, chatbot, Moid Hasan Beig"
        path="/llmoid"
      />
      <div className="llmoid-page" ref={containerRef}>
        <div className="llmoid-page-content">
          <h1>LLMoid</h1>
          <p>
            LLMoid is my LLM-powered alter ego. Curious about my career, side
            projects, or what I do for fun? Go ahead, ask it anything! It even
            knows my biking stats 🚴‍♂️
          </p>
          <div className="chat-container" ref={chatContainerRef}>
            <div className="chat-messages">
              {messages.length === 0 && (
                <div className="empty-state">
                  <p>Start a conversation by typing a message below.</p>
                </div>
              )}
              {messages.map(message => (
                <div
                  key={message.id}
                  className={`message ${message.type} ${message.isError ? 'error' : ''}`}
                >
                  <div className="message-content">
                    <ReactMarkdown>{message.text}</ReactMarkdown>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="message ai loading">
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="chat-input-container">
              <textarea
                className="chat-input"
                placeholder="Type your message here..."
                value={inputMessage}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                rows="1"
                disabled={isLoading}
              />
              <button
                className="send-button"
                onClick={handleSend}
                disabled={isLoading || !inputMessage.trim()}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LLMoid;
